import { EvidenceGradeBadge } from '@/components/shared/evidence-grade-badge'
import { KpiCard, KpiCardGroup } from '@/components/shared/kpi-card'
import { StatusBadge } from '@/components/shared/status-badge'
import { DELIVERY_STATE_LABEL } from '@/lib/growth/actions/lifecycle'
import { pct } from '@/lib/format'
import type { SamplePlanSummary } from '../data/plan-summary'
import type { SandboxSuggestion } from '../data/types'

interface PlanGroupProps {
  label: string
  /** Set when the group shows fewer rows than exist, so the cut is stated. */
  total?: number
  rows: SandboxSuggestion[]
  children: (suggestion: SandboxSuggestion) => React.ReactNode
}

/** One "Up next" / "In progress" group. A group with no rows renders nothing. */
function PlanGroup({ label, total, rows, children }: PlanGroupProps) {
  if (rows.length === 0) return null
  return (
    <section aria-label={label} className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <p className="u-label">{label}</p>
        {total !== undefined && total > rows.length && (
          <p className="text-label text-muted-foreground">
            Showing {rows.length} of {total}
          </p>
        )}
      </div>
      <ul className="rounded-lg border border-border bg-card">
        {rows.map((suggestion) => (
          <li
            key={suggestion.id}
            className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b border-border px-3 py-2.5 last:border-0"
          >
            <span className="min-w-0 flex-1 text-xs font-medium text-foreground">
              {suggestion.shortTitle}
            </span>
            {children(suggestion)}
          </li>
        ))}
      </ul>
    </section>
  )
}

interface PlanPageProps {
  summary: SamplePlanSummary
}

/**
 * The hero mini-dashboard's Plan page: the sample's headline numbers, then its Suggestions in the
 * app's "Up next" and "In progress" groups. Every figure comes from `getSamplePlanSummary`, so the
 * page agrees with the Plan tab and the Visibility page.
 */
export function PlanPage({ summary }: PlanPageProps) {
  return (
    <div className="space-y-4">
      <KpiCardGroup cols={3} className="grid-cols-2">
        <div data-visibility={summary.visibility} className="flex flex-col bg-card">
          <KpiCard label="Visibility" value={pct(summary.visibility / 100)} className="flex-1" />
        </div>
        <div className="flex flex-col bg-card">
          <KpiCard label="Suggestions" value={String(summary.suggestionCount)} className="flex-1" />
        </div>
        <div className="hidden flex-col bg-card sm:flex">
          <KpiCard label="Tasks live" value={String(summary.tasksLive)} className="flex-1" />
        </div>
      </KpiCardGroup>

      <PlanGroup label="Up next" total={summary.suggestionCount} rows={summary.upNext}>
        {(suggestion) => <EvidenceGradeBadge grade={suggestion.grade} />}
      </PlanGroup>

      <PlanGroup label="In progress" rows={summary.inProgress}>
        {() => <StatusBadge tone="success">{DELIVERY_STATE_LABEL.live_verified}</StatusBadge>}
      </PlanGroup>
    </div>
  )
}
